import { useGlobalBlockOrchestrator } from '@components/app/GlobalAppState';
import { useSplitLayout } from '@components/app/split-layout/layout';
import { type BlockAlias, type BlockName, useMaybeBlockId } from '@core/block';
import { fileTypeToBlockName } from '@core/constant/allBlocks';
import { useSplitNavigationHandler } from '@core/util/useSplitNavigationHandler';
import { Telemetry } from '@macro-inc/observability';
import { createCallback } from '@solid-primitives/rootless';
import type { ParentProps } from 'solid-js';

const blockNamesWithLocations = [
  'pdf',
  'canvas',
  'channel',
  'md',
  'task',
  'email',
  'chat',
  'database',
  'task',
] as const;
type BlockNameWithLocations = (typeof blockNamesWithLocations)[number];

export function isBlockNameWithLocation(
  name: BlockName | BlockAlias
): name is BlockNameWithLocations {
  return blockNamesWithLocations.includes(name as BlockNameWithLocations);
}

async function openLocation<T extends BlockNameWithLocations>(
  _blockName: T,
  id: string,
  params?: Record<string, string>
): Promise<void> {
  const blockOrchestrator = useGlobalBlockOrchestrator();
  const blockHandle = await blockOrchestrator.getBlockHandle(id);
  await blockHandle?.goToLocationFromParams(params ?? {});
}

function openLocationInBackground<T extends BlockNameWithLocations>(
  blockName: T,
  id: string,
  params?: Record<string, string>
) {
  openLocation(blockName, id, params).catch((error) =>
    Telemetry.error(error, { surface: 'block-link', blockName })
  );
}

export function openDocument(
  blockOrFileType: string,
  id: string,
  params?: Record<string, string>,
  inNewSplit?: boolean
) {
  const currentBlockId = useMaybeBlockId();
  const { openWithSplit } = useSplitLayout();

  const targetBlock = fileTypeToBlockName(blockOrFileType);
  if (!targetBlock) return;

  const hasParams = !!params && Object.keys(params).length > 0;

  if (
    currentBlockId === id &&
    hasParams &&
    isBlockNameWithLocation(targetBlock) &&
    !inNewSplit
  ) {
    openLocationInBackground(targetBlock, id, params);
    return;
  }

  const result = openWithSplit(
    { type: targetBlock, id, params },
    {
      preferNewSplit: inNewSplit,
      reopen: targetBlock === 'channel' && !hasParams ? 'latest' : undefined,
    }
  );

  if (isBlockNameWithLocation(targetBlock)) {
    openLocationInBackground(targetBlock, id, params);
  }
  return result;
}

export function BlockLink(
  props: ParentProps<{
    blockOrFileName: string;
    id: string;
    params?: Record<string, string>;
  }>
) {
  const open = createCallback((e: MouseEvent) => {
    let newSplit = e.shiftKey;
    openDocument(props.blockOrFileName, props.id, props.params, newSplit);
  });
  const navHandlers = useSplitNavigationHandler<HTMLSpanElement>(open);
  return <span {...navHandlers}>{props.children}</span>;
}
